import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Initialize media filtering on load
    this.#filterMediaByVariant();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the gallery or filtering media.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (source) {
      const newMediaGallery = source.querySelector('media-gallery');
      if (newMediaGallery) {
        this.replaceWith(newMediaGallery);
        return; // Exit after replacement to avoid duplicate filtering
      }
    }

    // Fallback to client-side filtering if no new gallery is provided
    this.#filterMediaByVariant(event.detail.variantId);
  };

  /**
   * Filters media elements based on the selected variant’s media IDs.
   *
   * @param {string|number} [variantId] - The ID of the selected variant (optional, fetched from form if not provided).
   */
  #filterMediaByVariant = async (variantId) => {
    // Skip if the hide-unselected-media setting is not enabled
    if (this.dataset.hideUnselectedMedia !== 'true') return;

    // Get the selected variant ID
    const selectedVariantId = variantId || this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    try {
      // Fetch product data to get variant media mappings
      const response = await fetch(`/products/${window.Shopify.productHandle}.js`);
      const product = await response.json();
      const variant = product.variants.find((v) => v.id == selectedVariantId);
      const variantMediaIds = variant?.media?.map((m) => m.id) || [];

      // Filter media elements
      this.refs.media?.forEach((mediaElement) => {
        const mediaId = parseInt(mediaElement.dataset.mediaId, 10);
        const isVisible = variantMediaIds.includes(mediaId) || !mediaElement.dataset.variantIds; // Show unassigned media
        mediaElement.style.display = isVisible ? 'block' : 'none';
      });

      // Update slideshow to reflect visible media
      this.slideshow?.select(0, undefined, { animate: false });
    } catch (error) {
      console.error('Error filtering media by variant:', error);
    }
  };

  /**
   * Gets the currently selected variant ID from the form or URL.
   *
   * @returns {string|number|null} The selected variant ID.
   */
  #getSelectedVariantId = () => {
    // Check form input first
    const variantSelector = this.closest('form')?.querySelector('select[name="id"], [data-variant-input] input:checked');
    if (variantSelector) {
      return variantSelector.value;
    }
    // Fallback to URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('variant');
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}